
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  respondAccessRequest,
  revokeAccessRequest,
} from "./notificationsThunks";

const DEBUG =
  String(import.meta.env.VITE_DEBUG_NOTIF_SLICE || "").toLowerCase() === "true";

/* =========================
   Helpers
========================= */

function getId(n) {
  return n?._id || n?.id || n?.notificationId;
}

function pickMeta(n) {
  return n?.metadata || n?.data || n?.payload || n?._raw?.metadata || {};
}

function hashStr(s = "") {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i);
  return (h >>> 0).toString(36);
}

function buildFingerprint(n) {
  const meta = pickMeta(n);
  const event = String(n?.event || meta?.event || n?.type || meta?.type || "").trim();

  const rid =
    meta?.rendezvousId ??
    meta?.rdvId ??
    meta?.appointmentId ??
    meta?.id ??
    n?.rendezvousId ??
    n?.rdvId ??
    null;

  const msgId =
    meta?.messageId ??
    n?.messageId ??
    meta?.chatId ??
    meta?.conversationId ??
    null;

  const doctorId = meta?.doctorId ?? n?.doctorId ?? meta?.medecinId ?? n?.medecinId ?? null;
  const patientId = meta?.patientId ?? n?.patientId ?? null;

  const startTime = meta?.startTime ?? n?.startTime ?? meta?.date ?? n?.date ?? null;

  const createdAt =
    n?.createdAt ||
    n?.date ||
    meta?.createdAt ||
    null;

  const parts = [
    event || "generic",
    rid != null ? String(rid) : "",
    msgId != null ? String(msgId) : "",
    doctorId != null ? String(doctorId) : "",
    patientId != null ? String(patientId) : "",
    startTime != null ? String(startTime) : "",
    createdAt != null ? String(createdAt) : "",
  ];

  const raw = parts.join("|").toLowerCase();
  const meaningful = raw.replace(/\|/g, "").trim();
  return meaningful ? raw : "";
}

function getStableId(n) {
  const real = getId(n);
  if (real != null && String(real).trim()) return String(real);

  const fp = buildFingerprint(n);
  if (fp) return `fp_${hashStr(fp)}`;

  return `tmp_${Math.random().toString(36).slice(2)}`;
}

function normalizeNotification(n) {
  if (!n) return null;

  const meta = pickMeta(n);
  const id = getStableId(n);

  const fromUserId =
    n.fromUserId ||
    n.from ||
    n.senderId ||
    meta?.fromUserId ||
    meta?.from ||
    meta?.senderId ||
    null;

  const fromUserName =
    n.fromUserName ||
    n.senderName ||
    n.fromName ||
    meta?.fromUserName ||
    meta?.senderName ||
    meta?.fromName ||
    null;

  const fromRole =
    n.fromRole || n.senderRole || meta?.fromRole || meta?.senderRole || null;

  const normalized = {
    id: String(id),
    type: n.type || n.event || n.category || "generic",
    event: n.event || meta?.event || null,
    targetId: n.targetId || meta?.targetId || null,
    secondaryId: n.secondaryId || meta?.secondaryId || null,
    title: n.title || n.messageTitle || "",
    message: n.message || n.text || n.body || "",

    fromUserId: fromUserId != null ? String(fromUserId) : null,
    fromUserName: fromUserName != null ? String(fromUserName) : null,
    fromRole: fromRole != null ? String(fromRole) : null,

    toUserId: n.toUserId || n.userId || n.recipientId || null,

    read: !!(n.read ?? n.isRead),
    createdAt: n.createdAt || n.date || meta?.createdAt || new Date().toISOString(),

    data: n.data || n.payload || n.metadata || null,
    _raw: n,
    _fp: buildFingerprint(n) ? `fp_${hashStr(buildFingerprint(n))}` : null,
  };

  if (DEBUG) {
    console.groupCollapsed("🧱 [SLICE] normalizeNotification");
    console.groupEnd();
  }

  return normalized;
}

function uniqMergeById(prev, incoming) {
  const map = new Map();
  for (const x of prev) map.set(String(x.id), x);
  for (const y of incoming) map.set(String(y.id), y);

  return Array.from(map.values()).sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
}

function existsNotif(stateItems, n) {
  const id = String(n?.id || "");
  if (id && stateItems.some((x) => String(x.id) === id)) return true;

  const fp = n?._fp;
  if (fp && stateItems.some((x) => x?._fp && String(x._fp) === String(fp))) return true;

  return false;
}

function updateAccessRequestStatus(items, requestId, status) {
  return items.map((notification) => {
    const metadata = pickMeta(notification);
    if (String(metadata?.requestId ?? "") !== String(requestId)) {
      return notification;
    }
    const nextMetadata = { ...metadata, status };
    return {
      ...notification,
      data: nextMetadata,
      _raw: {
        ...notification._raw,
        metadata: nextMetadata,
      },
    };
  });
}

/** ✅ extract real notification from WS/API wrapper */
function unwrapIncomingNotification(payload) {
  if (!payload) return null;

  if (payload?.notification && typeof payload.notification === "object") {
    return payload.notification;
  }

  if (payload?.data?.notification && typeof payload.data.notification === "object") {
    return payload.data.notification;
  }

  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    // only unwrap data if it looks like the notif itself
    if (
      payload.data?.id ||
      payload.data?._id ||
      payload.data?.type ||
      payload.data?.event ||
      payload.data?.title ||
      payload.data?.message
    ) {
      return payload.data;
    }
  }

  return payload;
}

const initialState = {
  items: [],
  total: 0,
  unreadCount: 0,
  status: "idle",
  error: null,
  lastFetch: { skip: 0, limit: 20 },
  userId: null,
  activeFetchRequestId: null,
  nextCursor: null,
  hasMore: false,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    pushNotification: (state, action) => {
      const payload = action.payload;

      // ✅ FIX: properly unwrap live socket payload
      const raw = unwrapIncomingNotification(payload);

      const n = normalizeNotification(raw);
      if (!n) return;
      if (state.userId && n.toUserId && String(n.toUserId) !== String(state.userId)) return;

      if (existsNotif(state.items, n)) {
        if (DEBUG) {
          console.groupCollapsed("♻️ [SLICE] pushNotification (DEDUPED)");
          console.groupEnd();
        }
        return;
      }

      if (DEBUG) {
        console.groupCollapsed("📥 [SLICE] pushNotification (INSERT)");
        console.groupEnd();
      }

      state.items = uniqMergeById([n], state.items);
      state.total = Math.max(state.total + 1, state.items.length);
      if (!n.read) state.unreadCount = (state.unreadCount || 0) + 1;
    },

    markReadLocal: (state, action) => {
      const id = String(action.payload);
      const idx = state.items.findIndex((x) => String(x.id) === id);

      if (idx >= 0 && !state.items[idx].read) {
        state.items[idx].read = true;
        state.unreadCount = Math.max(0, (state.unreadCount || 0) - 1);
      }
    },

    clearNotificationsError: (state) => {
      state.error = null;
    },

    resetNotifications: (state) => {
      state.items = [];
      state.total = 0;
      state.unreadCount = 0;
      state.status = "idle";
      state.error = null;
      state.lastFetch = { skip: 0, limit: 20 };
      state.userId = null;
      state.activeFetchRequestId = null;
      state.nextCursor = null;
      state.hasMore = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state, action) => {
        const nextUserId = action.meta.arg?.userId ? String(action.meta.arg.userId) : null;
        if (nextUserId && state.userId !== nextUserId) {
          state.items = [];
          state.total = 0;
          state.unreadCount = 0;
          state.lastFetch = { skip: 0, limit: 20 };
          state.userId = nextUserId;
          state.nextCursor = null;
          state.hasMore = false;
        }
        state.activeFetchRequestId = action.meta.requestId;
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        const requestedUserId = action.meta.arg?.userId;
        if (state.activeFetchRequestId !== action.meta.requestId ||
            (requestedUserId && String(requestedUserId) !== String(state.userId || ""))) return;
        state.activeFetchRequestId = null;
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Erreur lors du chargement des notifications";
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const requestedUserId = action.meta.arg?.userId || action.payload?.userId;
        if (state.activeFetchRequestId !== action.meta.requestId ||
            (state.userId && requestedUserId && String(requestedUserId) !== String(state.userId))) return;
        if (!state.userId && requestedUserId) state.userId = String(requestedUserId);
        state.activeFetchRequestId = null;
        const { items, total, unreadCount, skip, limit, cursor, nextCursor, hasMore } = action.payload || {};
        const normalized = (items || []).map(normalizeNotification).filter(Boolean);

        const merged = skip > 0 || cursor
          ? uniqMergeById(state.items, normalized)
          : uniqMergeById([], normalized);

        state.items = merged;
        state.total = Number.isFinite(total) ? total : state.items.length;
        state.unreadCount = Number.isFinite(unreadCount)
          ? unreadCount
          : state.items.reduce((n, x) => n + (x.read ? 0 : 1), 0);

        state.lastFetch = { skip: skip ?? 0, limit: limit ?? 20 };
        state.nextCursor = nextCursor || null;
        state.hasMore = Boolean(hasMore);
        state.status = "succeeded";

        if (DEBUG) {
          console.groupCollapsed("✅ [SLICE] fetchNotifications.fulfilled");
          console.groupEnd();
        }
      });

    builder.addCase(markNotificationRead.fulfilled, (state, action) => {
      const updated = action.payload;
      const id = String(updated?.id || updated?._id || "").trim();
      if (!id) return;

      const idx = state.items.findIndex((x) => String(x.id) === id);
      if (idx >= 0) {
        const wasUnread = !state.items[idx].read;
        // Marking a notification as read must not overwrite a newer local
        // access decision with stale metadata returned before the RMQ event
        // has updated ms-notification.
        state.items[idx].read = true;
        state.items[idx]._raw = {
          ...state.items[idx]._raw,
          read: true,
        };

        if (wasUnread) state.unreadCount = Math.max(0, (state.unreadCount || 0) - 1);
      } else {
        const n = normalizeNotification({ ...updated, id, read: true });
        if (n) state.items = uniqMergeById(state.items, [n]);
        state.unreadCount = state.items.reduce((n2, x) => n2 + (x.read ? 0 : 1), 0);
      }
    });

    builder.addCase(markAllNotificationsRead.fulfilled, (state) => {
      state.items = state.items.map((n) => ({ ...n, read: true }));
      state.unreadCount = 0;
    });

    builder.addCase(respondAccessRequest.fulfilled, (state, action) => {
      const requestId = String(action.payload?.id ?? '').trim();
      const status = String(action.payload?.status ?? '').toLowerCase();
      if (!requestId || !['accepted', 'rejected', 'resolved'].includes(status)) return;
      state.items = updateAccessRequestStatus(state.items, requestId, status);
    });

    builder.addCase(revokeAccessRequest.fulfilled, (state, action) => {
      const requestId = String(action.payload?.id ?? '').trim();
      const upstreamStatus = String(action.payload?.status ?? '').toLowerCase();
      const status = upstreamStatus === 'revoked' ? 'revoked' : 'resolved';
      if (!requestId) return;
      state.items = updateAccessRequestStatus(state.items, requestId, status);
    });
  },
});

export const {
  pushNotification,
  markReadLocal,
  clearNotificationsError,
  resetNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
